// useRosPositivesBolding — Task #228 (Leejoe 2026-06-28).
//
// 2-hour hack to make Brett's ROS card view live up to its design intent:
// positive complaints (the ones the patient actually reported in HPI/
// Subjective) should be **bolded** inside their system card.
//
// Why a render-time hack, not a structured data model:
//   - The "right" version stores per-system positives in a structured
//     `ros_findings` JSON, both card view + text view render from it.
//   - That's a 2-day refactor. Foothill Thursday demo is in 4 days.
//   - This hack ships in 2 hours: backend extracts positives from the note
//     via a Haiku pass; frontend walks the ros-grid DOM and wraps matching
//     substrings in <strong>.
//
// Behavior:
//   - Attaches a Quill text-change listener; debounces to 5000ms after the
//     local user's last edit before firing (see the guardrails below).
//   - POSTs the plain text of the doc to /api/ai/extract-ros-positives/.
//   - Walks the editor root for `.ros-card-desc` nodes; for each, wraps any
//     substring in the desc text that matches a positive for that system in
//     <strong data-ros-positive-wrap="1">. Idempotent — strips prior wraps
//     first.
//   - Wraps live INSIDE a contenteditable=false embed wrapper but on
//     contenteditable=true child nodes. The saved value uses textContent
//     (strips wraps) — purely visual decoration that survives until the
//     user retypes the cell.

import { useEffect, useRef } from 'react';
import { getBackendURL } from '../../components/env';

// Guardrails (2026-09-26): every call runs the note through the backend PHI scrub
// (~4 MB of server memory per KB of text) plus an AI call, so:
//   - only call when the editor actually shows ROS cards (nothing to bold otherwise),
//   - only react to the LOCAL user's edits (source 'user'), not to collaborators'
//     changes arriving over Yjs — each of their own tabs handles its own bolding,
//   - wait 5 s after the last keystroke instead of 1.5 s.
// The one pass on mount stays, so bolding appears when a chart is opened.
export const DEBOUNCE_MS = 5000;
export const MIN_CONTENT_CHARS = 200;

export function hasRosCards(root) {
  return Boolean(root && typeof root.querySelector === 'function' && root.querySelector('.ros-card-desc'));
}

function stripPriorBolds(root) {
  if (!root) return;
  const descs = root.querySelectorAll('.ros-card-desc');
  descs.forEach((desc) => {
    desc.querySelectorAll('strong[data-ros-positive-wrap="1"]').forEach((s) => {
      const parent = s.parentNode;
      if (!parent) return;
      while (s.firstChild) parent.insertBefore(s.firstChild, s);
      parent.removeChild(s);
    });
    desc.normalize();
  });
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function wrapMatchesInTextNode(textNode, terms) {
  const sorted = [...terms].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!sorted.length) return;
  const re = new RegExp(`(${sorted.map(escapeRegExp).join('|')})`, 'gi');
  const text = textNode.nodeValue || '';
  if (!re.test(text)) return;
  re.lastIndex = 0;
  const frag = document.createDocumentFragment();
  let lastIdx = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
    }
    const strong = document.createElement('strong');
    strong.setAttribute('data-ros-positive-wrap', '1');
    strong.style.fontWeight = '700';
    strong.textContent = m[0];
    frag.appendChild(strong);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIdx)));
  }
  const parent = textNode.parentNode;
  if (!parent) return;
  parent.insertBefore(frag, textNode);
  parent.removeChild(textNode);
}

function applyBolds(root, positivesByKey) {
  if (!root) return;
  const descs = root.querySelectorAll('.ros-card-desc');
  descs.forEach((desc) => {
    const key = (desc.getAttribute('data-ros') || '').toLowerCase();
    const terms = positivesByKey[key];
    if (!terms || !terms.length) return;
    const walker = document.createTreeWalker(desc, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);
    textNodes.forEach((tn) => wrapMatchesInTextNode(tn, terms));
  });
}

function getQuillPlainText(quill) {
  if (!quill || typeof quill.getText !== 'function') return '';
  try { return String(quill.getText() || ''); } catch (_) { return ''; }
}

export function useRosPositivesBolding(quill) {
  const debounceRef = useRef(null);
  const lastSentRef = useRef('');
  const cacheRef = useRef(new Map()); // contentHash → positives map

  useEffect(() => {
    if (!quill || typeof quill.on !== 'function') return undefined;

    const fire = async () => {
      if (!hasRosCards(quill.root)) return;
      const text = getQuillPlainText(quill);
      if (text.length < MIN_CONTENT_CHARS) return;
      if (lastSentRef.current === text) return;
      lastSentRef.current = text;

      let positives = cacheRef.current.get(text);
      if (!positives) {
        try {
          const res = await fetch(`${getBackendURL()}/api/ai/extract-ros-positives/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ document_content: text }),
          });
          if (!res.ok) return;
          const body = await res.json().catch(() => ({}));
          positives = body?.positives || {};
          cacheRef.current.set(text, positives);
          if (cacheRef.current.size > 20) {
            const firstKey = cacheRef.current.keys().next().value;
            cacheRef.current.delete(firstKey);
          }
        } catch (_) {
          return;
        }
      }
      const norm = {};
      for (const [k, v] of Object.entries(positives || {})) {
        norm[String(k).toLowerCase()] = Array.isArray(v) ? v : [];
      }
      const root = quill.root;
      if (!root) return;
      stripPriorBolds(root);
      applyBolds(root, norm);
    };

    const schedule = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fire, DEBOUNCE_MS);
    };

    // Quill passes (delta, oldDelta, source); remote Yjs updates and programmatic
    // changes arrive as 'api'/'silent', only typing in this tab is 'user'.
    const onTextChange = (_delta, _oldDelta, source) => {
      if (source === 'user') schedule();
    };

    // Initial pass on mount (after Yjs hydrates).
    schedule();
    quill.on('text-change', onTextChange);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      try { quill.off('text-change', onTextChange); } catch (_) {}
    };
  }, [quill]);
}
